// Developer maintains this — add a new entry per release; the newest entry's
// version MUST match package.json version.
//
// Entries are ordered NEWEST FIRST. The onboarding "What's New" popup and the
// Release History dialog both read from this single source of truth.

export interface ChangelogChange {
  type: 'feature' | 'improvement' | 'fix';
  text: string;
}

export interface ChangelogEntry {
  version: string;
  date: string;
  title?: string;
  changes: ChangelogChange[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.8.3',
    date: '2026-10-30',
    title: 'Fix: approving access requests for newer roles',
    changes: [
      { type: 'fix', text: 'Approving an access request and assigning a newer role (Catalog Manager, Catalog Member, Warehouse Member, or Data & Tech) no longer fails with a server error \u2014 all seven roles can now be assigned on approval' },
    ],
  },
  {
    version: '1.8.2',
    date: '2026-10-30',
    title: 'Carding: Blinkit NC as a non-carded shelf',
    changes: [
      { type: 'feature', text: 'SKUs sitting on the BLINKIT-NC shelf now show up as carding candidates and can be moved to a carded shelf, just like the Non Carded shelves' },
    ],
  },
  {
    version: '1.8.1',
    date: '2026-10-30',
    title: 'Release notes catch-up',
    changes: [
      { type: 'improvement', text: 'This What\u2019s New list now includes the Cost of Goods (COGS) release \u2014 see the details just below' },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-10-30',
    title: 'Cost of Goods (COGS) across the Bundle Catalog',
    changes: [
      { type: 'feature', text: 'Every bundle now shows its post-GST COGS \u2014 on the category cards, the Simple/Bundle table, and the version-history detail view' },
      { type: 'feature', text: 'Filter bundles by a COGS range (min/max), with \u201CNo cost data\u201D and \u201CPartial only\u201D options, on both the Bundle Categories and Simple/Bundle views' },
      { type: 'feature', text: 'Sort any list by COGS, high to low or low to high (bundles with no cost sort to the end)' },
      { type: 'feature', text: 'COGS is now included in the CSV exports \u2014 a total COGS column plus how many components are costed; the Detailed export also breaks out each component\u2019s cost and cost share' },
      { type: 'feature', text: 'The Simple/Bundle \u201CDetailed view\u201D shows a component-level cost breakdown with a cost-composition bar' },
      { type: 'improvement', text: 'Frozen (inactive) versions keep the COGS as of when they were created; versions that predate cost coverage are clearly labelled \u201Cas of Jun 2026 \u00b7 earliest available\u201D' },
      { type: 'improvement', text: 'Cleaner COGS presentation \u2014 cards and tables show the total with a calm \u201CN/M costed\u201D note instead of an alarm-styled tile' },
      { type: 'fix', text: 'Bundle Categories CSV export: the date and time now stay in one cell, DOC matches what the grid shows, and DRR/DOC read blank (not a misleading 0) when there\u2019s no sales data' },
      { type: 'fix', text: 'Actionables now close only when the issue is genuinely resolved \u2014 a brief data-sync gap no longer auto-closes an open item that then reappears later' },
    ],
  },
  {
    version: '1.7.2',
    date: '2026-10-27',
    title: 'Bundle Catalog polish and smoother updates',
    changes: [
      { type: 'improvement', text: 'Renamed Bundle Tool Tables to Bundle Catalog' },
      { type: 'improvement', text: 'Cleaner filter bar \u2014 shorter labels, search aligned under the tabs' },
      { type: 'feature', text: 'New \u201CUnmapped\u201D option in the Primary Tag filter to find bundles with no Primary set' },
      { type: 'improvement', text: 'Redesigned the bundle detail view \u2014 full-width, with Physical and Potential DOC' },
      { type: 'improvement', text: 'New, cleaner success and error notifications across the tool' },
      { type: 'fix', text: 'Sign-in and last-seen timestamps no longer get cut off on Access Control' },
      { type: 'fix', text: 'Button labels stay clearly readable, including when a button is inactive' },
      { type: 'improvement', text: 'Updates no longer sign you out \u2014 the tool refreshes in place' },
    ],
  },
  {
    version: '1.7.1',
    date: '2026-10-27',
    title: 'What\u2019s New dialog polish',
    changes: [
      { type: 'fix', text: 'Tidied the What\u2019s New list so tags and text line up cleanly' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-10-27',
    title: 'Debundle alerts, Catalog access, and stock-accuracy fixes',
    changes: [
      { type: 'feature', text: 'New actionable: debundle inactive stock back into components' },
      { type: 'feature', text: 'Filter actionables by team, with color-coded team tags' },
      { type: 'feature', text: 'Catalog Manager and Catalog Member can now create new bundle versions and categories' },
      { type: 'fix', text: 'Draft stock checks now count a SKU across all its shelves' },
      { type: 'fix', text: 'Carding and cyclic-count moves can no longer double-apply on a retry' },
      { type: 'improvement', text: 'Clearer error states with Retry on Activity Logs and Cyclic Count' },
    ],
  },
  {
    version: '1.6.2',
    date: '2026-10-26',
    title: 'Catalog import button fix',
    changes: [
      {
        type: 'fix',
        text: 'The Import (Bulk Upload) button in Bundle SKUs now shows for Catalog Manager and Catalog Member, matching their Bundle SKUs edit access — it was previously visible to admins only',
      },
    ],
  },
  {
    version: '1.6.1',
    date: '2026-10-26',
    title: 'Potential DOC refinement and Catalog import access',
    changes: [
      {
        type: 'improvement',
        text: 'Potential DOC now reflects only what you can still assemble from in-stock components (Can Be Made ÷ DRR), so it reads as true buildable headroom separate from units already on hand',
      },
      {
        type: 'fix',
        text: 'Catalog Manager and Catalog Member can now use Import in Bundle SKUs, matching their existing Bundle SKUs edit access',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-10-26',
    title: 'Low-stock alerts, and Carding & refresh fixes',
    changes: [
      {
        type: 'feature',
        text: 'Low days-of-cover actionables for active Simple variants: when a variant can still be assembled from in-stock components, the Warehouse Manager is asked to make more units; when it can’t, the Catalog Manager is asked to create a new version',
      },
      {
        type: 'improvement',
        text: 'Applied filters, search, sorting, and the page you’re on now stay put when you refresh — every list remembers where you were',
      },
      {
        type: 'fix',
        text: 'Refreshing inside any tab keeps you on that same page instead of bouncing you back to the home dashboard',
      },
      {
        type: 'fix',
        text: 'The Carding list now shows a variant’s linked packaging even when the packaging has no image, so mapped packaging no longer looks missing',
      },
      {
        type: 'fix',
        text: 'Actionables assigned to a role (like Catalog Manager or Warehouse Manager) now correctly show as a shared team task instead of being split across individual people',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-10-26',
    title: 'Access Control, a cleaner tool, and fixes',
    changes: [
      {
        type: 'feature',
        text: 'New admin-only Access Control center: see each user’s last sign-in, who’s online now, last-seen time, sign-in IP, and approximate location, with a per-user sign-in history',
      },
      {
        type: 'feature',
        text: 'Access Control is its own role permission — grant or restrict it per role from Roles & Permissions',
      },
      {
        type: 'improvement',
        text: 'Consistent look and feel across every tab — unified headers, typography, spacing, full-width layouts, and 25-per-page pagination with sortable columns everywhere',
      },
      {
        type: 'improvement',
        text: 'You always land on your home screen after signing in, never a blocked page; the dashboard banner now shows for everyone',
      },
      {
        type: 'improvement',
        text: 'Create User is full-width and cleaner, and now blocks Salty emails inline (Salty staff sign in with Google and request access for approval)',
      },
      {
        type: 'improvement',
        text: 'Mobile-friendlier screens, clearer error states with Retry, and better accessibility',
      },
      {
        type: 'fix',
        text: 'Button labels (like Next and Save changes) are now clearly readable on every button',
      },
      {
        type: 'fix',
        text: 'Inventory now clearly distinguishes “service unavailable” from “no items,” with a Retry option',
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-10-24',
    title: 'Roles & permissions, and a refreshed look',
    changes: [
      {
        type: 'feature',
        text: 'New role-based access: each role (Admin, Warehouse Manager, Warehouse Member, Catalog Manager, Catalog Member, Data & Tech, Photography) now has its own view/edit access per section, managed from Manage Users',
      },
      {
        type: 'feature',
        text: 'Added a Catalog Member role and updated Catalog Manager access (Bundle SKUs and image generation)',
      },
      {
        type: 'improvement',
        text: 'A consistent look across every tab — unified headers, typography, spacing, and full-width layouts',
      },
      {
        type: 'improvement',
        text: 'Header and footer stay fixed while only the content scrolls, so you never lose your place',
      },
      {
        type: 'improvement',
        text: 'Every list now pages (25 per page) instead of scrolling endlessly, with sortable columns where it helps',
      },
      {
        type: 'improvement',
        text: 'Clearer, full-width Inventory and Draft Bundles with sortable rows and readable timestamps',
      },
      {
        type: 'fix',
        text: 'Fixed being logged out when opening the Activities tab',
      },
      {
        type: 'fix',
        text: 'Fixed a sign-in error for some accounts after the roles update',
      },
      {
        type: 'fix',
        text: 'Button labels (like Save changes) are now clearly visible in every state',
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-10-22',
    title: 'Shared team actionables',
    changes: [
      {
        type: 'feature',
        text: 'Actionables can now be shared with a whole role — every warehouse manager sees the same task, and when any one of them marks it done it clears for everyone',
      },
      {
        type: 'improvement',
        text: 'Cleaner, consistent Rules page — each rule is a tidy card where you choose who it goes to (people or roles)',
      },
      {
        type: 'improvement',
        text: 'Your actionables list now pages instead of scrolling endlessly',
      },
      {
        type: 'fix',
        text: 'You now only see the actionable rules that are relevant to you',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-10-22',
    title: 'Smarter actionables',
    changes: [
      {
        type: 'feature',
        text: 'Assign actionable rules to a whole role (e.g. all warehouse managers), not just named people — new teammates in that role pick up tasks automatically',
      },
      {
        type: 'improvement',
        text: 'Access requests now show up in Actionables within seconds of someone requesting access, instead of waiting for the next day',
      },
      {
        type: 'improvement',
        text: 'Cleaner Actionables page with a clearer, simpler header',
      },
      {
        type: 'fix',
        text: 'Access request cards now show the name, email, time and reason clearly (time in IST)',
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-10-22',
    title: 'Google sign-in, access requests, and a faster Carding',
    changes: [
      {
        type: 'feature',
        text: 'Sign in with your Salty Google account — a cleaner, redesigned login',
      },
      {
        type: 'feature',
        text: 'New teammates can request access from the login screen; super-admins review and approve or reject requests in Manage Users',
      },
      {
        type: 'feature',
        text: 'Pending access requests now show up as actionables for super-admins',
      },
      {
        type: 'feature',
        text: 'Export all Carding packaging mappings to CSV',
      },
      {
        type: 'feature',
        text: 'New History tab in Carding shows what packaging changed for each SKU and when',
      },
      {
        type: 'improvement',
        text: 'Carding loads much faster and a page at a time, so it no longer stalls on large lists',
      },
      {
        type: 'improvement',
        text: 'Importing packaging mappings now flags bad SKUs or packaging SKUs and lets you download the rows that need fixing',
      },
      {
        type: 'improvement',
        text: 'Creating users is now limited to super-admins (non-Salty accounts only)',
      },
      {
        type: 'fix',
        text: 'Access requests now submit and show up reliably, and approve/reject work as expected',
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-10-21',
    title: 'Faster, clearer bundle operations',
    changes: [
      {
        type: 'feature',
        text: 'Direct Unicommerce inventory sync (shelfwise + not-found) replaces the old manual webhook',
      },
      {
        type: 'feature',
        text: 'Inventory sync now runs in the background with a live status and a syncing chip in the header',
      },
      {
        type: 'feature',
        text: 'Curated per-SKU packaging mappings in Carding, with an edit dialog and CSV import (downloadable template)',
      },
      {
        type: 'feature',
        text: 'A packaging catalog for Carding',
      },
      {
        type: 'improvement',
        text: 'Redesigned dashboard with a dark hero and grouped sections',
      },
      {
        type: 'improvement',
        text: 'App-wide Salty theme and Nimbus fonts',
      },
      {
        type: 'improvement',
        text: 'Clearer error messages when a carding move fails',
      },
      {
        type: 'fix',
        text: "Actionable 'Open parent' deep-link now jumps straight to the parent",
      },
      {
        type: 'fix',
        text: 'Stuck inventory syncs now self-heal instead of blocking work',
      },
    ],
  },
];
